import { useEffect, useRef } from 'react';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  logContainer: {
    maxHeight: 300,
    overflow: 'auto',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  logLine: {
    margin: 0,
    padding: '2px 0',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-all' as const,
  },
  summary: {
    padding: theme.spacing(2),
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
}));

export interface SyncSummary {
  totalProjects: number;
  existingCatalogInfo: number;
  mrsCreated: number;
  componentsRegistered: number;
  systemsCreated: number;
  errors: Array<{ project: string; error: string }>;
}

export interface SyncProgressLogProps {
  lines: string[];
  summary?: SyncSummary | null;
}

export function SyncProgressLog({ lines, summary }: SyncProgressLogProps) {
  const classes = useStyles();
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [lines.length]);

  if (lines.length === 0 && !summary) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Typography variant="subtitle2" gutterBottom>
        Log de Progresso
      </Typography>
      <Paper variant="outlined" className={classes.logContainer}>
        {lines.map((line, i) => (
          <p key={i} className={classes.logLine} data-testid="log-line">
            {line}
          </p>
        ))}
        <div ref={logEndRef} />
      </Paper>
      {summary && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div className={classes.summary} data-testid="sync-summary">
            <Typography variant="subtitle2" gutterBottom>
              Resumo da Sincronização
            </Typography>
            <div className={classes.summaryItem}>
              <span>Repositórios encontrados:</span>
              <strong>{summary.totalProjects}</strong>
            </div>
            <div className={classes.summaryItem}>
              <span>Já possuíam catalog-info.yaml:</span>
              <strong>{summary.existingCatalogInfo}</strong>
            </div>
            <div className={classes.summaryItem}>
              <span>catalog-info.yaml criados:</span>
              <strong>{summary.mrsCreated}</strong>
            </div>
            <div className={classes.summaryItem}>
              <span>Componentes registrados:</span>
              <strong>{summary.componentsRegistered}</strong>
            </div>
            <div className={classes.summaryItem}>
              <span>Systems criados:</span>
              <strong>{summary.systemsCreated ?? 0}</strong>
            </div>
            {summary.errors.length > 0 && (
              <div className={classes.summaryItem}>
                <span>Erros:</span>
                <strong>{summary.errors.length}</strong>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
